/**
 * features/students/StudentHistoryTab.tsx
 * ------------------------------------------------------------------
 * Round 10 — per-student audit trail.
 *
 * Reads from reportAuditLog and shows every event that touched THIS
 * student: comment saves, editor corrections, flags raised, flag
 * status changes, discipline records, marks edits.
 *
 * The history is read-only and shown chronologically (newest first).
 * Each row can be expanded to show full details: actor, target,
 * before/after text for comment edits, flag reason/notes, device
 * fingerprint.
 *
 * Permission: gated by the parent's canViewStudentProfile check —
 * if the user can see this student's profile, they can see the
 * history. This is intentional (history is a strictly weaker
 * permission than viewing academic data).
 *
 * Indexing strategy: the audit log doesn't currently have a
 * studentId field — it stores student names in `details.studentNames`
 * and the new Round-10 code adds `details.studentIds`. We query
 * targetType === 'comment' | 'discipline' | (flag entries) and
 * filter client-side. For a 600-student school over a full year
 * this is well under 5k entries — fine for browser-side scan.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import {
  Clock,
  User as UserIcon,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Flag as FlagIcon,
  ShieldAlert,
  PenSquare,
  Inbox,
  Filter,
  RefreshCcw,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { Card, Spinner } from '@/components/ui/primitives';
import { ClipboardCheck } from 'lucide-react';
import type { Student } from '@/types';

interface Props {
  student: Student;
}

interface AuditEvent {
  id: string;
  action: string;
  category: string;
  actorId?: string;
  actorEmail?: string;
  actorName?: string;
  actorRole?: string;
  targetId?: string;
  targetType?: string;
  targetName?: string;
  details?: Record<string, unknown>;
  device?: {
    userAgent?: string;
    platform?: string;
    displayMode?: string;
  };
  createdAt: string;
}

/** Configuration for each audit-event family shown in the history. */
const FAMILY_META = {
  comment: {
    label: 'Comment',
    Icon: MessageSquare,
    tone: 'text-blue-700 bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
  },
  flag: {
    label: 'Flag',
    Icon: FlagIcon,
    tone: 'text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
  },
  discipline: {
    label: 'Discipline',
    Icon: ShieldAlert,
    tone: 'text-red-700 bg-red-50 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
  },
  marks: {
    label: 'Marks',
    Icon: PenSquare,
    tone: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
  },
} as const;
type Family = keyof typeof FAMILY_META;

/** Verb-to-human-label mapping for the row title. */
const ACTION_LABEL: Record<string, string> = {
  comments_saved: 'Comment saved',
  comments_edited_by_editor: 'Comment edited by editor',
  flag_raised: 'Flag raised',
  flag_status_changed: 'Flag status changed',
  discipline_created: 'Discipline record created',
  discipline_updated: 'Discipline record updated',
  marks_saved: 'Marks saved',
};

export default function StudentHistoryTab({ student }: Props) {
  const { subjects } = useData();
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [familyFilter, setFamilyFilter] = useState<Family | 'all'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  /** Decide which event family a given audit row belongs to. */
  function familyOf(e: AuditEvent): Family | null {
    if (e.action === 'comments_saved' || e.action === 'comments_edited_by_editor') return 'comment';
    if (e.action === 'flag_raised' || e.action === 'flag_status_changed') return 'flag';
    if (e.action === 'discipline_created' || e.action === 'discipline_updated') return 'discipline';
    if (e.action === 'marks_saved') return 'marks';
    return null;
  }

  /** Predicate: does this event involve our student? */
  function touchesStudent(e: AuditEvent): boolean {
    // Discipline: targetType === 'discipline' and targetName usually contains the student name.
    // The DisciplineModule audit calls pass targetName: student.fullName.
    if (e.action === 'discipline_created' || e.action === 'discipline_updated') {
      return e.targetName === student.fullName;
    }
    // Flag: details.studentId (set by Round-10-patched raiseFlag).
    // Fallback to details.studentName for older entries.
    if (e.action === 'flag_raised' || e.action === 'flag_status_changed') {
      const d = e.details ?? {};
      if (d.studentId === student.id) return true;
      if (d.studentName === student.fullName) return true;
      return false;
    }
    // Comment saves: details.studentIds (Round 10) OR details.studentNames (legacy).
    if (e.action === 'comments_saved' || e.action === 'comments_edited_by_editor') {
      const d = e.details ?? {};
      const ids = d.studentIds;
      if (Array.isArray(ids) && ids.includes(student.id)) return true;
      const names = d.studentNames;
      if (Array.isArray(names) && names.includes(student.fullName)) return true;
      // Editor flow also uses `details.edits` array of { studentId, studentName, ... }
      const edits = d.edits;
      if (Array.isArray(edits)) {
        for (const x of edits as Array<{ studentId?: string; studentName?: string }>) {
          if (x.studentId === student.id) return true;
          if (x.studentName === student.fullName) return true;
        }
      }
      return false;
    }
    // Marks: detail shape varies — best effort by classId match on student's current class
    // is too noisy. We exclude marks_saved from the per-student view by default — they're
    // a class-wide event, not student-specific.
    return false;
  }

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const snap = await getDocs(
        query(
          collection(db, COLLECTIONS.auditLog),
          orderBy('createdAt', 'desc'),
          limit(1000),
        ),
      );
      const all: AuditEvent[] = snap.docs.map(
        (d) => ({ id: d.id, ...d.data() } as AuditEvent),
      );
      const relevant = all.filter((e) => {
        if (!familyOf(e)) return false;
        return touchesStudent(e);
      });
      setEvents(relevant);
    } catch (e) {
      setErr((e as Error).message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student.id]);

  const filtered = useMemo(() => {
    if (familyFilter === 'all') return events;
    return events.filter((e) => familyOf(e) === familyFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, familyFilter]);

  const counts = useMemo(() => {
    const out: Record<Family, number> = {
      comment: 0,
      flag: 0,
      discipline: 0,
      marks: 0,
    };
    for (const e of events) {
      const f = familyOf(e);
      if (f) out[f]++;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  function formatTimestamp(iso: string): string {
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  }

  /* ============ render a single event ============ */
  function renderEvent(e: AuditEvent) {
    const fam = familyOf(e);
    if (!fam) return null;
    const meta = FAMILY_META[fam];
    const Icon = meta.Icon;
    const expanded = expandedId === e.id;
    const title = ACTION_LABEL[e.action] ?? e.action;
    const subtitle = renderEventSubtitle(e);

    return (
      <div
        key={e.id}
        className="hover:bg-gray-50/60 dark:hover:bg-gray-700/20 transition-colors"
      >
        <button
          onClick={() => setExpandedId(expanded ? null : e.id)}
          className="w-full text-left px-4 py-3 flex items-start gap-3"
        >
          <span
            className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg border ${meta.tone}`}
            title={meta.label}
          >
            <Icon size={14} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-gray-900 dark:text-white">
                {title}
              </span>
              {subtitle && (
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  · {subtitle}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              <span className="inline-flex items-center gap-1">
                <Clock size={11} />
                {formatTimestamp(e.createdAt)}
              </span>
              {e.actorName && (
                <span className="inline-flex items-center gap-1">
                  <UserIcon size={11} />
                  {e.actorName}
                  {e.actorRole && (
                    <span className="text-gray-400">({e.actorRole})</span>
                  )}
                </span>
              )}
            </div>
          </div>
          <span className="shrink-0 mt-1">
            {expanded ? (
              <ChevronDown size={15} className="text-gray-400" />
            ) : (
              <ChevronRight size={15} className="text-gray-400" />
            )}
          </span>
        </button>

        {expanded && renderEventDetails(e)}
      </div>
    );
  }

  function renderEventSubtitle(e: AuditEvent): string {
    const d = e.details ?? {};
    if (e.action === 'comments_saved' || e.action === 'comments_edited_by_editor') {
      const field = d.field;
      if (field === 'hodComment') return 'HOY comment';
      if (field === 'classTeacherComment') return 'Class teacher comment';
      if (e.action === 'comments_edited_by_editor') return 'Editor correction';
      return 'Comment update';
    }
    if (e.action === 'flag_raised') {
      const parts: string[] = [];
      if (d.target) parts.push(String(d.target));
      if (d.reason) parts.push(String(d.reason));
      return parts.join(' · ');
    }
    if (e.action === 'flag_status_changed') {
      const from = d.fromStatus;
      const to = d.toStatus;
      if (from && to) return `${from} → ${to}`;
      return '';
    }
    if (e.action === 'discipline_created' || e.action === 'discipline_updated') {
      const parts: string[] = [];
      if (d.category) parts.push(String(d.category));
      if (d.severity) parts.push(String(d.severity));
      return parts.join(' · ');
    }
    return '';
  }

  /* ============ expanded details ============ */
  function renderEventDetails(e: AuditEvent) {
    const d = e.details ?? {};

    // Per-student edit diff for the editor flow OR the new Round-10 comment-save diff
    const edits = Array.isArray(d.edits) ? (d.edits as Array<{
      studentId?: string;
      studentName?: string;
      field?: string;
      before?: string;
      after?: string;
    }>) : null;
    const myEdits = edits?.filter(
      (x) => x.studentId === student.id || x.studentName === student.fullName,
    );

    return (
      <div className="px-4 pb-4 ml-11 space-y-2 text-xs">
        {/* Diff blocks (one per comment field changed for THIS student) */}
        {myEdits && myEdits.length > 0 && (
          <div className="space-y-2">
            {myEdits.map((edit, i) => (
              <div
                key={i}
                className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40"
              >
                {edit.field && (
                  <div className="px-3 py-1.5 text-[10px] uppercase font-semibold tracking-wide text-gray-500 border-b border-gray-100 dark:border-gray-700/50">
                    {edit.field}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 divide-x divide-gray-100 dark:divide-gray-700/50">
                  <div className="p-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                      Before
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap break-words">
                      {edit.before?.trim() || (
                        <span className="text-gray-400 italic">empty</span>
                      )}
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-50/40 dark:bg-emerald-900/10">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 mb-1">
                      After
                    </p>
                    <p className="text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap break-words">
                      {edit.after?.trim() || (
                        <span className="text-gray-400 italic">empty</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Context grid — flag reason, notes, term info, etc. */}
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mt-2">
          {Boolean(d.year) && <ContextRow label="Year" value={String(d.year)} />}
          {Boolean(d.term) && <ContextRow label="Term" value={String(d.term)} />}
          {Boolean(d.field) && <ContextRow label="Field" value={String(d.field)} />}
          {Boolean(d.target) && <ContextRow label="Target" value={String(d.target)} />}
          {Boolean(d.reason) && <ContextRow label="Reason" value={String(d.reason)} />}
          {Boolean(d.notes) && <ContextRow label="Notes" value={String(d.notes)} full />}
          {Boolean(d.subjectName) && (
            <ContextRow label="Subject" value={String(d.subjectName)} />
          )}
          {Boolean(d.category) && <ContextRow label="Category" value={String(d.category)} />}
          {Boolean(d.severity) && <ContextRow label="Severity" value={String(d.severity)} />}
          {Boolean(d.fromStatus) && Boolean(d.toStatus) && (
            <ContextRow
              label="Status"
              value={`${String(d.fromStatus)} → ${String(d.toStatus)}`}
            />
          )}
        </dl>

        {/* Device footer — when + where, in muted text */}
        {(e.actorEmail || e.device?.platform) && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 pt-2 border-t border-gray-100 dark:border-gray-700/40 flex flex-wrap gap-x-3 gap-y-0.5">
            {e.actorEmail && <span>{e.actorEmail}</span>}
            {e.device?.platform && <span>· {e.device.platform}</span>}
            {e.device?.displayMode && (
              <span>· {e.device.displayMode}</span>
            )}
          </p>
        )}
      </div>
    );
  }

  /* ============ pieces ============ */
  function renderEntrancePanel() {
    const et = student.admissions?.entranceTest;
    if (!et || !et.taken) return null;
    const subjectName = (id: string) => subjects.find((s) => s.id === id)?.name ?? id;
    const results = et.results ?? [];
    return (
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <ClipboardCheck size={16} className="text-primary" />
            Entrance test
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Recorded at admission
            {et.testDate ? ` · ${new Date(et.testDate).toLocaleDateString()}` : ''}
          </p>
        </div>
        <div className="px-5 py-4">
          {results.length === 0 ? (
            <p className="text-sm text-gray-500">
              Marked as taken, but no subject marks were recorded.
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {results.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-gray-800 dark:text-gray-100">
                    {subjectName(r.subjectId)}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white tabular-nums">
                    {r.mark}
                    {typeof r.outOf === 'number' ? ` / ${r.outOf}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          {et.notes && (
            <p className="text-xs text-gray-500 mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
              {et.notes}
            </p>
          )}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {renderEntrancePanel()}
      <Card className="p-0 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Clock size={16} className="text-primary" />
            Activity history
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Every comment edit, flag, and discipline action involving{' '}
            <strong>{student.fullName}</strong>. Read-only.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCcw size={12} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filter chips */}
      <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2 flex-wrap text-xs">
        <Filter size={12} className="text-gray-400" />
        <FilterChip
          active={familyFilter === 'all'}
          label="All"
          count={events.length}
          onClick={() => setFamilyFilter('all')}
        />
        {(Object.keys(FAMILY_META) as Family[]).map((f) => (
          <FilterChip
            key={f}
            active={familyFilter === f}
            label={FAMILY_META[f].label}
            count={counts[f]}
            onClick={() => setFamilyFilter(f)}
          />
        ))}
      </div>

      {/* Body */}
      {loading ? (
        <div className="p-8 text-center">
          <Spinner label="Loading history…" />
        </div>
      ) : err ? (
        <div className="p-6 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20">
          {err}
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center">
          <Inbox size={28} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No activity recorded for this student
            {familyFilter !== 'all' && ` in "${FAMILY_META[familyFilter].label}"`}.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {filtered.map(renderEvent)}
        </div>
      )}
    </Card>
    </div>
  );
}

/* ===================== small helpers ===================== */

function FilterChip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? 'bg-primary text-white border-primary'
          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
      }`}
    >
      {label}
      <span
        className={`px-1.5 py-0 rounded-full text-[10px] font-semibold ${
          active
            ? 'bg-white/20 text-white'
            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function ContextRow({
  label,
  value,
  full,
}: {
  label: string;
  value: string;
  full?: boolean;
}) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <dt className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
        {label}
      </dt>
      <dd className="text-gray-700 dark:text-gray-300 break-words">{value}</dd>
    </div>
  );
}
